import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

/* =========================================
   Helpers
   ========================================= */

const obtenerNombreCategoria = (categoria) =>
  String(
    categoria?.nombre ||
      categoria?.name ||
      "Categoría sin nombre"
  ).trim();

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleUpperCase("es");

const compararCategorias = (categoriaA, categoriaB) =>
  obtenerNombreCategoria(categoriaA).localeCompare(
    obtenerNombreCategoria(categoriaB),
    "es",
    {
      sensitivity: "base",
      numeric: true,
    }
  );

const escaparHTML = (valor) =>
  String(valor ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

/* =========================================
   Componente
   ========================================= */

function SelectorCategoriaMoodle({
  plataforma = "cursos",
  onChange,
}) {
  const [categorias, setCategorias] = useState([]);
  const [modo, setModo] = useState("existente");

  const [
    categoriaSeleccionada,
    setCategoriaSeleccionada,
  ] = useState(null);

  const [categoriaPadre, setCategoriaPadre] =
    useState(null);

  const [nombreNueva, setNombreNueva] =
    useState("");

  const [loading, setLoading] = useState(false);

  const [
    cargandoCategorias,
    setCargandoCategorias,
  ] = useState(false);

  /* =========================================
     Construir árbol jerárquico

     Mantiene:
     - padre
       - curso
         - división

     Cada nivel se ordena alfabéticamente.
     ========================================= */

  const categoriasJerarquicas = useMemo(() => {
    if (!Array.isArray(categorias)) {
      return [];
    }

    const categoriasValidas = categorias
      .filter((categoria) => categoria?.id != null)
      .map((categoria) => ({
        ...categoria,
        id: Number(categoria.id),
        parent: Number(categoria.parent || 0),
      }));

    const mapaCategorias = new Map();

    categoriasValidas.forEach((categoria) => {
      mapaCategorias.set(Number(categoria.id), {
        ...categoria,
        hijos: [],
      });
    });

    const categoriasRaiz = [];

    mapaCategorias.forEach((categoria) => {
      const parentId = Number(categoria.parent || 0);

      /*
       * Si el padre existe, agregamos la categoría como hija.
       * Si no existe o es raíz, la dejamos como categoría principal.
       */
      if (
        parentId !== 0 &&
        mapaCategorias.has(parentId)
      ) {
        mapaCategorias
          .get(parentId)
          .hijos.push(categoria);
      } else {
        categoriasRaiz.push(categoria);
      }
    });

    const ordenarRecursivamente = (lista) => {
      return [...lista]
        .sort(compararCategorias)
        .map((categoria) => ({
          ...categoria,
          hijos: ordenarRecursivamente(
            categoria.hijos || []
          ),
        }));
    };

    return ordenarRecursivamente(categoriasRaiz);
  }, [categorias]);

  /* =========================================
     Convertir árbol en opciones para select

     Conserva profundidad y jerarquía.
     ========================================= */

  const opcionesCategorias = useMemo(() => {
    const resultado = [];

    const recorrer = (
      listaCategorias,
      nivel = 0,
      rutaPadres = []
    ) => {
      listaCategorias.forEach((categoria) => {
        const nombre =
          obtenerNombreCategoria(categoria);

        const ruta = [...rutaPadres, nombre];

        resultado.push({
          ...categoria,
          nivel,
          ruta,
          rutaTexto: ruta.join(" / "),
          tieneHijos:
            Array.isArray(categoria.hijos) &&
            categoria.hijos.length > 0,
        });

        if (
          Array.isArray(categoria.hijos) &&
          categoria.hijos.length > 0
        ) {
          recorrer(
            categoria.hijos,
            nivel + 1,
            ruta
          );
        }
      });
    };

    recorrer(categoriasJerarquicas);

    return resultado;
  }, [categoriasJerarquicas]);

  /* =========================================
     Cargar categorías
     ========================================= */

  const fetchCategorias = async () => {
    setCargandoCategorias(true);

    try {
      const respuesta = await axios.post(
        `https://wwwp.isepsantafe.net.ar/moodleservice/categorias?plataforma=${plataforma}`
      );

      const categoriasRecibidas = Array.isArray(
        respuesta.data?.categorias
      )
        ? respuesta.data.categorias
        : [];

      setCategorias(categoriasRecibidas);

      return categoriasRecibidas;
    } catch (error) {
      console.error(
        "Error al cargar categorías Moodle:",
        error
      );

      setCategorias([]);

      await Swal.fire({
        icon: "error",
        title: "No se pudieron cargar las categorías",
        text:
          error?.response?.data?.mensaje ||
          error?.response?.data?.error ||
          error?.message ||
          "Ocurrió un problema al consultar las categorías de Moodle.",
      });

      return [];
    } finally {
      setCargandoCategorias(false);
    }
  };

  useEffect(() => {
    setCategoriaSeleccionada(null);
    setCategoriaPadre(null);
    setNombreNueva("");
    setModo("existente");

    if (typeof onChange === "function") {
      onChange(null);
    }

    fetchCategorias();
  }, [plataforma]);

  /* =========================================
     Cambiar modo
     ========================================= */

  const cambiarModo = (nuevoModo) => {
    setModo(nuevoModo);
    setCategoriaSeleccionada(null);
    setCategoriaPadre(null);
    setNombreNueva("");

    if (typeof onChange === "function") {
      onChange(null);
    }
  };

  /* =========================================
     Seleccionar categoría existente
     ========================================= */

  const seleccionarExistente = (idCategoria) => {
    if (!idCategoria) {
      setCategoriaSeleccionada(null);

      if (typeof onChange === "function") {
        onChange(null);
      }

      return;
    }

    const categoriaEncontrada =
      opcionesCategorias.find(
        (categoria) =>
          String(categoria.id) ===
          String(idCategoria)
      );

    setCategoriaSeleccionada(
      categoriaEncontrada || null
    );

    if (typeof onChange === "function") {
      onChange(categoriaEncontrada || null);
    }
  };

  /* =========================================
     Seleccionar categoría padre
     ========================================= */

  const seleccionarCategoriaPadre = (
    idCategoria
  ) => {
    if (!idCategoria) {
      setCategoriaPadre(null);
      return;
    }

    const categoriaEncontrada =
      opcionesCategorias.find(
        (categoria) =>
          String(categoria.id) ===
          String(idCategoria)
      );

    setCategoriaPadre(
      categoriaEncontrada || null
    );
  };

  /* =========================================
     Crear categoría
     ========================================= */

  const crearCategoria = async () => {
    const nombreLimpio = nombreNueva.trim();

    if (!nombreLimpio) {
      await Swal.fire({
        icon: "warning",
        title: "Ingresá un nombre",
        text: "Debés escribir el nombre de la nueva categoría.",
      });

      return;
    }

    const parentId = categoriaPadre?.id
      ? Number(categoriaPadre.id)
      : 0;

    /*
     * Solamente considera duplicado cuando el nombre
     * coincide dentro del mismo padre.
     */
    const categoriaExistente =
      opcionesCategorias.find((categoria) => {
        return (
          normalizarTexto(
            obtenerNombreCategoria(categoria)
          ) === normalizarTexto(nombreLimpio) &&
          Number(categoria.parent || 0) ===
            parentId
        );
      });

    if (categoriaExistente) {
      await Swal.fire({
        icon: "warning",
        title: "La categoría ya existe",
        html: `
          <div style="text-align:left;">
            <p>
              Ya existe una categoría llamada:
            </p>

            <p>
              <strong>${escaparHTML(
                nombreLimpio
              )}</strong>
            </p>

            <p>
              Ubicación:
              <strong>${escaparHTML(
                categoriaExistente.rutaTexto
              )}</strong>
            </p>
          </div>
        `,
      });

      return;
    }

    const confirmacion = await Swal.fire({
      icon: "question",
      title: "¿Crear esta categoría?",
      showCancelButton: true,
      confirmButtonText: "Sí, crear",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
      html: `
        <div style="text-align:left;">
          <div style="
            padding: 12px;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            background: #f8f9fa;
          ">
            <p>
              <strong>Nueva categoría:</strong><br>
              ${escaparHTML(nombreLimpio)}
            </p>

            <p style="margin-bottom:0;">
              <strong>Se creará dentro de:</strong><br>
              ${escaparHTML(
                categoriaPadre?.rutaTexto ||
                  "Raíz de Moodle"
              )}
            </p>
          </div>
        </div>
      `,
    });

    if (!confirmacion.isConfirmed) {
      return;
    }

    try {
      setLoading(true);

      Swal.fire({
        title: "Creando categoría",
        text: "Comunicando con Moodle...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const respuesta = await axios.post(
        "https://wwwp.isepsantafe.net.ar/moodleservice/categorias/crear",
        null,
        {
          params: {
            plataforma,
            parent: parentId,
            name: nombreLimpio,
          },
        }
      );

      const categoriaDevuelta =
        respuesta.data?.categoria ||
        respuesta.data;

      const idNuevaCategoria =
        categoriaDevuelta?.id ||
        respuesta.data?.id;

      if (!idNuevaCategoria) {
        throw new Error(
          "El servidor no devolvió el ID de la categoría creada."
        );
      }

      const nuevaCategoria = {
        ...categoriaDevuelta,
        id: Number(idNuevaCategoria),
        nombre:
          categoriaDevuelta?.nombre ||
          categoriaDevuelta?.name ||
          nombreLimpio,
        parent: parentId,
        creada: true,
      };

      await fetchCategorias();

      setCategoriaSeleccionada(
        nuevaCategoria
      );

      setCategoriaPadre(null);
      setNombreNueva("");
      setModo("existente");

      if (typeof onChange === "function") {
        onChange(nuevaCategoria);
      }

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Categoría creada",
        html: `
          <div style="text-align:left;">
            <p>
              <strong>Categoría:</strong><br>
              ${escaparHTML(
                nuevaCategoria.nombre
              )}
            </p>

            <p>
              <strong>ID Moodle:</strong><br>
              ${escaparHTML(
                nuevaCategoria.id
              )}
            </p>

            <p style="margin-bottom:0;">
              <strong>Ubicación:</strong><br>
              ${escaparHTML(
                categoriaPadre?.rutaTexto ||
                  "Raíz de Moodle"
              )}
            </p>
          </div>
        `,
      });
    } catch (error) {
      console.error(
        "Error al crear categoría Moodle:",
        error
      );

      Swal.close();

      await Swal.fire({
        icon: "error",
        title: "No se pudo crear la categoría",
        text:
          error?.response?.data?.mensaje ||
          error?.response?.data?.error ||
          error?.message ||
          "Ocurrió un problema al crear la categoría.",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================================
     Renderizar texto de opción

     Ejemplo:
     📁 Escuela Superior
        ├─ 📁 Curso de Comisarios
        │  └─ División 01
     ========================================= */

  const renderizarNombreOpcion = (
    categoria,
    mostrarCursos = true
  ) => {
    const nivel = Number(categoria.nivel || 0);

    const sangria =
      nivel === 0
        ? ""
        : `${"　".repeat(nivel - 1)}└─ `;

    const icono = categoria.tieneHijos
      ? "📁"
      : nivel >= 2
      ? "📄"
      : "📂";

    const cantidadCursos =
      categoria.cursos != null &&
      mostrarCursos
        ? ` (${categoria.cursos})`
        : "";

    return `${sangria}${icono} ${obtenerNombreCategoria(
      categoria
    )}${cantidadCursos}`;
  };

  return (
    <div
      style={{
        border: "1px solid #d7dce1",
        borderRadius: 10,
        padding: 16,
        marginBottom: 16,
        backgroundColor: "#fff",
      }}
    >
      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h6 className="mb-1">
            📁 Ubicación en Moodle
          </h6>

          <small className="text-muted">
            Elegí dónde se crearán el curso y sus
            divisiones.
          </small>
        </div>

        {cargandoCategorias && (
          <span
            className="spinner-border spinner-border-sm"
            role="status"
          />
        )}
      </div>

      {/* =========================================
          Selector de modo
          ========================================= */}

      <div
        className="btn-group w-100 mb-3"
        role="group"
      >
        <input
          id="categoria-moodle-existente"
          className="btn-check"
          type="radio"
          name="modoCategoriaMoodle"
          checked={modo === "existente"}
          disabled={loading}
          onChange={() =>
            cambiarModo("existente")
          }
        />

        <label
          className="btn btn-outline-primary"
          htmlFor="categoria-moodle-existente"
        >
          Usar existente
        </label>

        <input
          id="categoria-moodle-nueva"
          className="btn-check"
          type="radio"
          name="modoCategoriaMoodle"
          checked={modo === "nueva"}
          disabled={loading}
          onChange={() => cambiarModo("nueva")}
        />

        <label
          className="btn btn-outline-primary"
          htmlFor="categoria-moodle-nueva"
        >
          Crear nueva
        </label>
      </div>

      {/* =========================================
          Categoría existente
          ========================================= */}

      {modo === "existente" && (
        <>
          <label
            htmlFor="selector-categoria-existente"
            className="form-label fw-bold"
          >
            Categoría donde se creará el curso
          </label>

          <select
            id="selector-categoria-existente"
            className="form-select"
            value={
              categoriaSeleccionada?.id || ""
            }
            disabled={
              cargandoCategorias || loading
            }
            onChange={(event) =>
              seleccionarExistente(
                event.target.value
              )
            }
          >
            <option value="">
              {cargandoCategorias
                ? "Cargando categorías..."
                : "Seleccionar ubicación"}
            </option>

            {opcionesCategorias.map(
              (categoria) => (
                <option
                  key={categoria.id}
                  value={categoria.id}
                >
                  {renderizarNombreOpcion(
                    categoria,
                    true
                  )}
                </option>
              )
            )}
          </select>

          {categoriaSeleccionada && (
            <div className="alert alert-success mt-3 mb-0">
              <div className="fw-bold mb-1">
                Ubicación seleccionada
              </div>

              <div>
                {
                  categoriaSeleccionada.rutaTexto
                }
              </div>

              <small>
                ID Moodle:{" "}
                {categoriaSeleccionada.id}
              </small>
            </div>
          )}

          {!cargandoCategorias &&
            opcionesCategorias.length === 0 && (
              <div className="alert alert-warning mt-3 mb-0">
                No se encontraron categorías en
                Moodle.
              </div>
            )}
        </>
      )}

      {/* =========================================
          Nueva categoría
          ========================================= */}

      {modo === "nueva" && (
        <>
          <label
            htmlFor="selector-categoria-padre"
            className="form-label fw-bold"
          >
            Crear dentro de
          </label>

          <select
            id="selector-categoria-padre"
            className="form-select mb-3"
            value={categoriaPadre?.id || ""}
            disabled={
              cargandoCategorias || loading
            }
            onChange={(event) =>
              seleccionarCategoriaPadre(
                event.target.value
              )
            }
          >
            <option value="">
              📁 Raíz principal de Moodle
            </option>

            {opcionesCategorias.map(
              (categoria) => (
                <option
                  key={categoria.id}
                  value={categoria.id}
                >
                  {renderizarNombreOpcion(
                    categoria,
                    false
                  )}
                </option>
              )
            )}
          </select>

          {categoriaPadre && (
            <div className="alert alert-info py-2">
              <strong>Ubicación:</strong>{" "}
              {categoriaPadre.rutaTexto}
            </div>
          )}

          <label
            htmlFor="nombre-nueva-categoria"
            className="form-label fw-bold"
          >
            Nombre de la categoría
          </label>

          <input
            id="nombre-nueva-categoria"
            type="text"
            className="form-control mb-3"
            placeholder="Ejemplo: Ciclo lectivo 2027"
            value={nombreNueva}
            disabled={loading}
            onChange={(event) =>
              setNombreNueva(event.target.value)
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                crearCategoria();
              }
            }}
          />

          <div className="d-grid">
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                loading ||
                cargandoCategorias ||
                !nombreNueva.trim()
              }
              onClick={crearCategoria}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />

                  Creando...
                </>
              ) : (
                <>➕ Crear categoría</>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default SelectorCategoriaMoodle;